import { z } from 'zod';
import { isCalendarDate, normalizeName } from './people.js';
import type { FamilyPlanView } from './training.js';

/** Sport-extensible catalog of event types; the labels are translated in the locale catalogs. */
export const eventTypes = [
  'on_ice_training',
  'off_ice_training',
  'match',
  'assessment',
  'meeting',
] as const;
export type EventType = (typeof eventTypes)[number];

export const eventStatuses = ['draft', 'published', 'cancelled', 'completed'] as const;
export type EventStatus = (typeof eventStatuses)[number];

export const rsvpResponses = ['yes', 'no', 'tentative'] as const;
export type RsvpResponse = (typeof rsvpResponses)[number];

export const attendanceStatuses = [
  'present',
  'late',
  'absent_excused',
  'absent_unexcused',
  'not_recorded',
] as const;
export type AttendanceStatus = (typeof attendanceStatuses)[number];

export const resourceKinds = ['rink', 'zone', 'room', 'field'] as const;
export type ResourceKind = (typeof resourceKinds)[number];

export const surfaceTypes = ['ice', 'dryland', 'other'] as const;
export type SurfaceType = (typeof surfaceTypes)[number];

/** Guardians may answer until this many minutes before the start unless an event says otherwise. */
export const defaultRsvpDeadlineMinutes = 120;
/** Series are generated this many days ahead unless the series says otherwise. */
export const defaultSeriesHorizonDays = 90;
export const maximumSeriesHorizonDays = 180;

const calendarDateSchema = z.string().refine(isCalendarDate);
const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const titleSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(200));
const nameSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(200));
const timeZoneSchema = z.string().min(1).max(64);
const reasonSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(500));
const optionalLocationSchema = z
  .string()
  .transform(normalizeName)
  .pipe(z.string().max(300))
  .transform((value) => (value === '' ? null : value))
  .nullable();

/**
 * Who an event is for: a team, a training group or one athlete. The union is deduplicated, so a few
 * players picked from different teams are simply several athlete entries.
 */
export const audienceEntrySchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('team'), teamSeasonId: z.uuid() }),
  z.strictObject({ kind: z.literal('group'), trainingGroupId: z.uuid() }),
  z.strictObject({ kind: z.literal('athlete'), athleteId: z.uuid() }),
]);
export type AudienceEntry = z.infer<typeof audienceEntrySchema>;

/** Room for whole teams plus a hand-picked squad drawn from several rosters. */
export const maximumAudienceEntries = 200;

export const createVenueRequestSchema = z.strictObject({
  name: nameSchema,
  departmentId: z.uuid().nullable().default(null),
  /** Defaults to the club's time zone. */
  timezone: timeZoneSchema.optional(),
});
export type CreateVenueRequest = z.infer<typeof createVenueRequestSchema>;

/** A rink with bookable zones is a resource with child resources; the tree cannot be rearranged later. */
export const createResourceRequestSchema = z.strictObject({
  venueId: z.uuid(),
  parentResourceId: z.uuid().nullable().default(null),
  name: nameSchema,
  kind: z.enum(resourceKinds),
  surfaceType: z.enum(surfaceTypes).nullable().default(null),
  capacity: z.number().int().min(0).max(100_000).nullable().default(null),
  /** Time before a booking that nobody else may use the resource. */
  setupBufferMinutes: z.number().int().min(0).max(240).default(0),
  /** Time after a booking, for example for resurfacing. */
  cleanupBufferMinutes: z.number().int().min(0).max(240).default(0),
});
export type CreateResourceRequest = z.infer<typeof createResourceRequestSchema>;

const eventSharedShape = {
  departmentId: z.uuid(),
  eventType: z.enum(eventTypes),
  title: titleSchema,
  /** Defaults to the venue's or the club's time zone. */
  timezone: timeZoneSchema.optional(),
  location: optionalLocationSchema.default(null),
  resourceId: z.uuid().nullable().default(null),
  capacity: z.number().int().min(0).max(100_000).nullable().default(null),
  arrivalMinutesBefore: z.number().int().min(0).max(240).nullable().default(null),
  rsvpDeadlineMinutes: z.number().int().min(0).max(43_200).default(defaultRsvpDeadlineMinutes),
  audience: z.array(audienceEntrySchema).max(maximumAudienceEntries).default([]),
  /** Staff by membership; names are not shown because staff accounts carry no personal profile. */
  staffOrganizationUserIds: z.array(z.uuid()).max(20).default([]),
};

/**
 * Creates a draft event. Times are the wall-clock date and times in the event's time zone, so the
 * browser does no zone arithmetic; the end falls on `endDate` (default: the same day).
 */
export const createEventRequestSchema = z
  .strictObject({
    ...eventSharedShape,
    date: calendarDateSchema,
    startTime: localTimeSchema,
    endTime: localTimeSchema,
    endDate: calendarDateSchema.optional(),
  })
  .refine(
    (request) =>
      (request.endDate ?? request.date) > request.date || request.endTime > request.startTime,
  );
export type CreateEventRequest = z.infer<typeof createEventRequestSchema>;

export const seriesEditScopes = ['this', 'future'] as const;
export type SeriesEditScope = (typeof seriesEditScopes)[number];

/**
 * Changes an event. For an occurrence of a series, `scope` chooses between a single exception
 * (default) and this occurrence plus all later ones, which splits the series.
 */
export const updateEventRequestSchema = z.strictObject({
  scope: z.enum(seriesEditScopes).default('this'),
  eventType: z.enum(eventTypes).optional(),
  title: titleSchema.optional(),
  timezone: timeZoneSchema.optional(),
  date: calendarDateSchema.optional(),
  startTime: localTimeSchema.optional(),
  endTime: localTimeSchema.optional(),
  endDate: calendarDateSchema.optional(),
  location: optionalLocationSchema.optional(),
  resourceId: z.uuid().nullable().optional(),
  capacity: z.number().int().min(0).max(100_000).nullable().optional(),
  arrivalMinutesBefore: z.number().int().min(0).max(240).nullable().optional(),
  rsvpDeadlineMinutes: z.number().int().min(0).max(43_200).optional(),
  audience: z.array(audienceEntrySchema).max(maximumAudienceEntries).optional(),
  staffOrganizationUserIds: z.array(z.uuid()).max(20).optional(),
  /** Accept that some athletes are already expected at another event at the same time. */
  confirmAthleteOverlaps: z.boolean().default(false),
});
export type UpdateEventRequest = z.infer<typeof updateEventRequestSchema>;

export const publishEventRequestSchema = z.strictObject({
  confirmAthleteOverlaps: z.boolean().default(false),
});
export type PublishEventRequest = z.infer<typeof publishEventRequestSchema>;

/**
 * Whether the participants' families are told about a cancellation or a restoration. The notice is
 * queued with the change; delivery by e-mail or push is the next work package (WP08).
 */
const notifyParticipantsSchema = z.boolean().default(true);

export const cancelEventRequestSchema = z.strictObject({
  reason: reasonSchema,
  scope: z.enum(seriesEditScopes).default('this'),
  notifyParticipants: notifyParticipantsSchema,
});
export type CancelEventRequest = z.infer<typeof cancelEventRequestSchema>;

/** Brings back a cancelled event that has not started yet, as it was before the cancellation. */
export const restoreEventRequestSchema = z.strictObject({
  notifyParticipants: notifyParticipantsSchema,
  /** Accept that some athletes are now expected at another event at the same time. */
  confirmAthleteOverlaps: z.boolean().default(false),
});
export type RestoreEventRequest = z.infer<typeof restoreEventRequestSchema>;

/** A weekly recurrence held at a fixed local start time. */
export const createEventSeriesRequestSchema = z
  .strictObject({
    ...eventSharedShape,
    startsOn: calendarDateSchema,
    startTime: localTimeSchema,
    durationMinutes: z.number().int().min(5).max(1440),
    intervalWeeks: z.number().int().min(1).max(8).default(1),
    weekdays: z
      .array(z.number().int().min(1).max(7))
      .min(1)
      .max(7)
      .refine((weekdays) => new Set(weekdays).size === weekdays.length),
    untilOn: calendarDateSchema.nullable().default(null),
    occurrenceCount: z.number().int().min(1).max(1000).nullable().default(null),
    /** Publish each generated occurrence (those that cannot reserve their resource stay drafts). */
    publishOccurrences: z.boolean().default(false),
    horizonDays: z
      .number()
      .int()
      .min(1)
      .max(maximumSeriesHorizonDays)
      .default(defaultSeriesHorizonDays),
  })
  .refine((request) => request.untilOn === null || request.untilOn >= request.startsOn)
  .refine((request) => request.untilOn === null || request.occurrenceCount === null);
export type CreateEventSeriesRequest = z.infer<typeof createEventSeriesRequestSchema>;

/**
 * Changes how often a series happens from `fromDate` on: weekdays, every how many weeks, the local
 * time and the last day. Earlier occurrences are untouched. Later occurrences on a day the new rule
 * still contains keep their identity, answers and attendance and move to the new time; those on a
 * day it no longer contains are cancelled with `reason`; missing ones are generated.
 */
export const rescheduleEventSeriesRequestSchema = z
  .strictObject({
    fromDate: calendarDateSchema,
    startTime: localTimeSchema,
    durationMinutes: z.number().int().min(5).max(1440),
    intervalWeeks: z.number().int().min(1).max(8),
    weekdays: z
      .array(z.number().int().min(1).max(7))
      .min(1)
      .max(7)
      .refine((weekdays) => new Set(weekdays).size === weekdays.length),
    untilOn: calendarDateSchema.nullable(),
    /** Shown on the occurrences that are cancelled because their day left the schedule. */
    reason: reasonSchema,
    notifyParticipants: notifyParticipantsSchema,
  })
  .refine((request) => request.untilOn === null || request.untilOn >= request.fromDate);
export type RescheduleEventSeriesRequest = z.infer<typeof rescheduleEventSeriesRequestSchema>;

export const respondRsvpRequestSchema = z.strictObject({
  athleteId: z.uuid(),
  response: z.enum(rsvpResponses),
  /** Required when staff correct an answer after the deadline. */
  reason: reasonSchema.optional(),
});
export type RespondRsvpRequest = z.infer<typeof respondRsvpRequestSchema>;

export const attendanceItemSchema = z.strictObject({
  participantId: z.uuid(),
  status: z.enum(attendanceStatuses),
  /** The row version the coach saw; a newer edit by someone else is reported, never overwritten. */
  version: z.number().int().min(1),
});

export const updateAttendanceRequestSchema = z
  .strictObject({ items: z.array(attendanceItemSchema).min(1).max(200) })
  .refine(
    (request) =>
      new Set(request.items.map((item) => item.participantId)).size === request.items.length,
  );
export type UpdateAttendanceRequest = z.infer<typeof updateAttendanceRequestSchema>;

export interface VenueView {
  id: string;
  departmentId: string | null;
  name: string;
  timezone: string;
  version: number;
  resources: ResourceView[];
}

export interface ResourceView {
  id: string;
  venueId: string;
  parentResourceId: string | null;
  name: string;
  kind: ResourceKind;
  surfaceType: SurfaceType | null;
  capacity: number | null;
  setupBufferMinutes: number;
  cleanupBufferMinutes: number;
}

export type AudienceView =
  | { kind: 'team'; teamSeasonId: string; name: string }
  | { kind: 'group'; trainingGroupId: string; name: string }
  | { kind: 'athlete'; athleteId: string; name: string };

/** An event as staff see it. Instants are RFC 3339; the wall-clock fields are in `timezone`. */
export interface EventView {
  id: string;
  departmentId: string;
  seriesId: string | null;
  /** Local date of the occurrence this event belongs to, `YYYY-MM-DD`. */
  seriesDate: string | null;
  /** True once this occurrence was edited on its own and no longer follows the series. */
  isException: boolean;
  eventType: EventType;
  title: string;
  timezone: string;
  startsAt: string;
  endsAt: string;
  date: string;
  startTime: string;
  endTime: string;
  arrivalAt: string | null;
  location: string | null;
  resourceId: string | null;
  resourceName: string | null;
  /** False while a chosen resource could not be reserved; such an event cannot be published. */
  resourceReserved: boolean;
  capacity: number | null;
  rsvpDeadlineMinutes: number;
  rsvpDeadlineAt: string;
  status: EventStatus;
  cancelReason: string | null;
  /** When the event was first published; it stays set after a cancellation. */
  publishedAt: string | null;
  /** Row version; send it in `If-Match` when changing the event. */
  version: number;
  audience: AudienceView[];
  staffOrganizationUserIds: string[];
  /** Active participants; zero until the event is published. */
  participantCount: number;
  /** The published training plan version this event uses, if any. */
  trainingPlanVersionId: string | null;
}

export interface EventSeriesView {
  id: string;
  departmentId: string;
  previousSeriesId: string | null;
  eventType: EventType;
  title: string;
  timezone: string;
  intervalWeeks: number;
  weekdays: number[];
  startsOn: string;
  untilOn: string | null;
  occurrenceCount: number | null;
  startTime: string;
  durationMinutes: number;
  resourceId: string | null;
  location: string | null;
  capacity: number | null;
  arrivalMinutesBefore: number | null;
  rsvpDeadlineMinutes: number;
  horizonDays: number;
  publishOccurrences: boolean;
  /** Last local day for which occurrences were generated. */
  generatedThrough: string | null;
  status: 'active' | 'ended';
  version: number;
}

export interface GenerationConflictView {
  /** Local date of the occurrence that could not reserve its resource. */
  date: string;
  resourceId: string;
}

export interface SeriesGenerationView {
  series: EventSeriesView;
  created: number;
  /** Occurrences that already existed and were left as they are. */
  existing: number;
  /** Occurrences created as drafts because their resource was already taken. */
  conflicts: GenerationConflictView[];
  /** Occurrences left as drafts because they could not be published (for example a staff clash). */
  unpublished: number;
}

export interface SeriesRescheduleView {
  /** The series that holds the occurrences from `fromDate` on (a new one when the series split). */
  series: EventSeriesView;
  /** Occurrences that stayed because their day is still in the schedule. */
  kept: number;
  /** Occurrences cancelled because their day left the schedule. */
  cancelled: number;
  /** Occurrences generated for days that are new in the schedule. */
  created: number;
  conflicts: GenerationConflictView[];
}

export interface RsvpView {
  response: RsvpResponse;
  recordedAt: string;
  version: number;
  correctionReason: string | null;
}

export interface EventParticipantView {
  participantId: string;
  athleteId: string;
  givenName: string;
  familyName: string;
  sources: ('team' | 'group' | 'athlete')[];
  rsvp: RsvpView | null;
  attendance: { status: AttendanceStatus; version: number };
}

export interface ParticipantChangeView {
  athleteId: string;
  givenName: string;
  familyName: string;
}

/** What a refresh would change after membership changes; applying it never erases history. */
export interface ParticipantRefreshView {
  toAdd: ParticipantChangeView[];
  /** Participants no longer in the audience without an answer or attendance record. */
  toRemove: ParticipantChangeView[];
  /** Participants no longer in the audience that stay because they have an answer or attendance. */
  retained: ParticipantChangeView[];
}

export type AttendanceOutcome = 'updated' | 'conflict' | 'not_found';

export interface AttendanceRowResult {
  participantId: string;
  outcome: AttendanceOutcome;
  /** The stored status and version after the request (the winning edit on a conflict). */
  status: AttendanceStatus | null;
  version: number | null;
}

export interface AttendanceBatchView {
  results: AttendanceRowResult[];
  updated: number;
  conflicts: number;
}

export interface AttendanceSummaryRow {
  athleteId: string;
  givenName: string;
  familyName: string;
  present: number;
  late: number;
  absentExcused: number;
  absentUnexcused: number;
  /** Present plus late. */
  attended: number;
  /** Present, late and both absence kinds. */
  recorded: number;
  /** `attended / recorded`, or null when nothing was recorded. */
  rate: number | null;
}

export interface AttendanceSummaryView {
  from: string;
  to: string;
  rows: AttendanceSummaryRow[];
  totals: Omit<AttendanceSummaryRow, 'athleteId' | 'givenName' | 'familyName'>;
}

export const attendanceHistoryQuerySchema = z.strictObject({
  status: z.enum(attendanceStatuses).optional(),
  search: z.string().trim().max(100).optional(),
  offset: z.coerce.number().int().min(0).max(100000).default(0),
});

export interface AttendanceHistoryRow {
  participantId: string;
  athleteId: string;
  givenName: string;
  familyName: string;
  eventId: string;
  eventTitle: string;
  date: string;
  startsAt: string;
  timezone: string;
  status: AttendanceStatus;
}

export interface AttendanceHistoryView {
  from: string;
  to: string;
  items: AttendanceHistoryRow[];
  nextOffset: number | null;
}

/** One child's place in an event as the family sees it. */
export interface MyEventChildView {
  athleteId: string;
  givenName: string;
  familyName: string;
  participantId: string;
  rsvp: RsvpResponse | null;
  /** The link allows answering, the event is published and the deadline has not passed. */
  canRespond: boolean;
}

/** Published information only: no capacity, staff, audience or internal fields. */
export interface MyEventView {
  id: string;
  eventType: EventType;
  title: string;
  timezone: string;
  startsAt: string;
  endsAt: string;
  date: string;
  startTime: string;
  endTime: string;
  arrivalAt: string | null;
  location: string | null;
  resourceName: string | null;
  status: EventStatus;
  cancelReason: string | null;
  rsvpDeadlineAt: string;
  /** Selected summaries of the attached plan; null when none is attached or none is shared. */
  plan: FamilyPlanView | null;
  children: MyEventChildView[];
}

export interface StaffOptionView {
  organizationUserId: string;
  /** True for the signed-in person's own membership. */
  isYou: boolean;
}
